import CompanyName from "components/Companies/CompanyName";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useCompany } from "hooks/useCompany";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import PageHeader from "components/PageLayout";
import { StatusBadge } from "components/Account/StatusBadge";
import Skeleton from "components/Skeleton";
import OverviewTab from "./tabs/OverviewTab";
import ApplicationTab from "./tabs/ApplicationTab";
import MidsTab from "./tabs/MidsTab";
import TeamTab from "pages/settings/TeamTab";
import ActivityTab from "./tabs/ActivityTab";
import DisputifierTab from "./tabs/DisputifierTab";
import FilesTab from "./tabs/FilesTab";
import MidApplicationsTab from "./tabs/MidApplicationsTab";
import TicketsTab from "./tabs/TicketsTab";
import CompanyCredentialsTab from "./tabs/CredentialsTab";
import { ArrowLeft } from "lucide-react";

// Ordered by how a record is actually worked: who they are, what they applied
// for, what they got, what is happening now, then the supporting material. The
// two tabs nobody uses day to day sit at the end rather than in the middle of
// that path — Disputifier is slated to be replaced by a scoped Risk view, and
// Activity is waiting on the audit-log overhaul.
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "application", label: "Registration" },
  { key: "mid-applications", label: "Applications" },
  { key: "mids", label: "MIDs" },
  { key: "tickets", label: "Tickets" },
  // SOUND-709 — ownership is a companyId ON the credential and it is exactly
  // one, so "this client's credentials" is a real question. Sits beside Files
  // as supporting material rather than in the working path.
  { key: "credentials", label: "Credentials" },
  { key: "files", label: "Files" },
  { key: "team", label: "Team" },
  { key: "disputifier", label: "Disputifier" },
  { key: "activity", label: "Activity" },
];

export function CompanyDetails({ companyId: companyIdProp } = {}) {
  const { id } = useParams();
  const companyId = companyIdProp || id;
  const navigate = useNavigate();
  const { company, loading, error, refetch } = useCompany(companyId);
  const auth = useAuth();
  const internal = isInternal(auth.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Keep tab state in sync with the `?tab=` query param so deep-links
  // (e.g. navigate after Create Client) land on the correct tab.
  useEffect(() => {
    const queryTab = searchParams.get("tab");
    if (queryTab && queryTab !== activeTab) {
      setActiveTab(queryTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (nextKey) => {
    setActiveTab(nextKey);
    // Reflect the tab change in the URL so page reloads preserve it.
    const params = new URLSearchParams(searchParams);
    if (nextKey === "overview") {
      params.delete("tab");
    } else {
      params.set("tab", nextKey);
    }
    setSearchParams(params, { replace: true });
  };

  // Filter tabs based on user type, role, and company features
  const features = company?.features || [];
  const isAdmin = internal || auth.user?.role === "admin";
  const visibleTabs = TABS.filter((t) => {
    if (t.key === "activity") return internal;
    if (t.key === "mid-applications") return internal || features.includes("application-pipeline");
    if (t.key === "mids") return internal || features.includes("mid-manager");
    if (t.key === "disputifier") return internal || features.includes("disputifier");
    if (t.key === "tickets") return internal || features.includes("ticketing");
    if (t.key === "team") return isAdmin;
    return true;
  });

  if (loading) {
    return (
      <div>
        <PageHeader title={<Skeleton.Rect className="h-8 w-64" />} />
        <div className="container py-6 space-y-6">
          <Skeleton.Rect className="h-48 w-full" />
          <Skeleton.Rect className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen flex items-center justify-center text-destructive">
        Failed to load company.
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={<CompanyName company={company} />}
        subtitle={
          !companyIdProp ? (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Companies
            </button>
          ) : undefined
        }
        badge={<StatusBadge status={company.status} />}
        meta={[
          { label: "Type", value: company.companyType },
          ...(company.tier ? [{ label: "Tier", value: company.tier }] : []),
          ...(company.slug ? [{ label: "Slug", value: company.slug }] : []),
        ]}
        tabs={visibleTabs}
        activeKey={activeTab}
        onTabChange={handleTabChange}
      />

      <div className="container py-6">
        {activeTab === "overview" && (
          <OverviewTab company={company} loading={false} refetch={refetch} />
        )}
        {activeTab === "application" && (
          <ApplicationTab companyId={companyId} />
        )}
        {activeTab === "mid-applications" && (
          <MidApplicationsTab
            companyId={company.companyId}
            isPartnerView={company.companyType === "partner"}
          />
        )}
        {activeTab === "mids" && (
          <MidsTab
            companyId={company.companyId}
            isPartnerView={company.companyType === "partner"}
          />
        )}
        {activeTab === "team" && (
          <TeamTab companyId={company.companyId} />
        )}
        {activeTab === "credentials" && (
          <CompanyCredentialsTab companyId={companyId} />
        )}
        {activeTab === "files" && (
          <FilesTab companyId={company.companyId} />
        )}
        {activeTab === "disputifier" && (
          <DisputifierTab companyId={company.companyId} />
        )}
        {activeTab === "tickets" && (
          <TicketsTab
            companyId={company.companyId}
            companyName={company.name}
            isPartnerView={company.companyType === "partner"}
          />
        )}
        {activeTab === "activity" && (
          <ActivityTab companyId={company.companyId} />
        )}
      </div>
    </div>
  );
}
